import { useEffect } from 'react';
import { useMe } from '../hooks/useMe';

interface Props {
  open: boolean;
  currentTeamId: string | null;
  onSelect: (toTeamId: string) => void;
  onClose: () => void;
}

/**
 * "Move to …" dialog, driven by `useMe().groups`. The list of destinations
 * is the caller's visible groups (companies + their own personal partition
 * and any personals they're shared with). The option equal to the current
 * teamId is rendered disabled as a "current" marker.
 */
export function MoveScopeDialog({ open, currentTeamId, onSelect, onClose }: Props) {
  const { me } = useMe();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const groups = [...(me?.groups ?? [])].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  return (
    <div
      className="dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Move to group"
      onClick={onClose}
    >
      <div className="move-scope-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>Move to…</h3>
        {groups.length === 0 ? (
          <p className="muted">No other groups available.</p>
        ) : (
          <ul>
            {groups.map((g) => (
              <li key={g.id}>
                <button
                  onClick={() => {
                    onSelect(g.id);
                    onClose();
                  }}
                  disabled={g.id === currentTeamId}
                  className="move-scope-option"
                >
                  <span className="scope-swatch" style={{ backgroundColor: g.color }} />
                  <span>{g.displayName}</span>
                  {g.id === currentTeamId && <span className="muted small">current</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
